import { create } from 'zustand'
import { EMPTY_PUSH_PROMPT_POLICY, type PushPromptPolicy } from '@/lib/push/pushPromptDecision'

const STORAGE_KEY = 'buildlog.pushPrompt'
const PERSISTED_VERSION = 1

type PolicyByUser = Record<string, PushPromptPolicy>

type PersistedShape = {
  version: number
  byUser: PolicyByUser
}

type PushPromptState = {
  /**
   * EPHEMERAL — deliberately not persisted. Persisting visibility would let a
   * reload restore a visible card while bypassing every gate condition: it
   * would reappear before the reveal delay and possibly while offline, already
   * registered, denied, or with no user loaded yet.
   */
  isVisible: boolean
  /**
   * Also EPHEMERAL. Set when an Enable attempt failed for a reason that is not
   * a user decision (denied, unsupported, transient error). Without it the gate
   * would re-evaluate, still see its cached `prompt` permission, and pop the
   * card again a few seconds later — a loop. Deliberately NOT persisted: none of
   * those outcomes is a choice, so the next app start may ask again (and a real
   * denial will by then be visible in the permission state itself).
   */
  isSessionSuppressed: boolean
  /**
   * Policy is keyed per user id. On a shared browser this keeps one account's
   * snoozes from silently suppressing the prompt for the next person to log in.
   */
  byUser: PolicyByUser
  show: () => void
  /** Contextual hide (registered elsewhere, went offline, unmounted). No policy change. */
  hide: () => void
  /** Stop asking until the next app start. Not a user choice, so nothing persists. */
  suppressForSession: () => void
  /** Drop session suppression — on account change, and on a successful enable. */
  resetSessionSuppression: () => void
  /** "Not now" — starts the cooldown. */
  snooze: (userId: number) => void
  /** "Don't ask again", and pressing Disable in notification settings. */
  dismissForever: (userId: number) => void
  /** Successful Enable — clears suppression so a later Disable/re-enable behaves freshly. */
  clearSuppression: (userId: number) => void
}

const readPersistedState = (): PolicyByUser => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as Partial<PersistedShape>
    if (parsed.version !== PERSISTED_VERSION || typeof parsed.byUser !== 'object') return {}
    return parsed.byUser ?? {}
  } catch {
    return {}
  }
}

const persistState = (byUser: PolicyByUser): void => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: PERSISTED_VERSION, byUser } satisfies PersistedShape),
    )
  } catch {
    // localStorage unavailable — non-critical, policy degrades to session-only
  }
}

/** Read one user's policy out of the persisted map. Missing user → empty policy. */
export function getPushPromptPolicy(byUser: PolicyByUser, userId: number | null): PushPromptPolicy {
  if (userId === null) return EMPTY_PUSH_PROMPT_POLICY
  return byUser[String(userId)] ?? EMPTY_PUSH_PROMPT_POLICY
}

export const usePushPromptStore = create<PushPromptState>(set => {
  const updatePolicy = (userId: number, patch: Partial<PushPromptPolicy>) =>
    set(state => {
      const key = String(userId)
      const next: PolicyByUser = {
        ...state.byUser,
        [key]: { ...(state.byUser[key] ?? EMPTY_PUSH_PROMPT_POLICY), ...patch },
      }
      persistState(next)
      return { byUser: next, isVisible: false }
    })

  return {
    isVisible: false,
    isSessionSuppressed: false,
    byUser: readPersistedState(),
    show: () => set({ isVisible: true }),
    hide: () => set({ isVisible: false }),
    suppressForSession: () => set({ isVisible: false, isSessionSuppressed: true }),
    resetSessionSuppression: () => set({ isSessionSuppressed: false }),
    snooze: userId => updatePolicy(userId, { lastSnoozedAt: Date.now() }),
    dismissForever: userId => updatePolicy(userId, { dismissedForever: true }),
    // Clears BOTH kinds of suppression — the persisted policy and the session
    // flag. Anything less would leave a successful enable half-suppressed.
    clearSuppression: userId =>
      set(state => {
        const next: PolicyByUser = {
          ...state.byUser,
          [String(userId)]: { ...EMPTY_PUSH_PROMPT_POLICY },
        }
        persistState(next)
        return { byUser: next, isVisible: false, isSessionSuppressed: false }
      }),
  }
})

/** Test seam — restores a clean store between cases. */
export const resetPushPromptStoreForTests = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
  usePushPromptStore.setState({ isVisible: false, isSessionSuppressed: false, byUser: {} })
}
